import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Title } from "react-admin";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SearchIcon from "@mui/icons-material/Search";
import DirectionsCarOutlinedIcon from "@mui/icons-material/DirectionsCarOutlined";
import PageHeader from "./PageHeader";
import { callDriverFunction, loadSupplierDrivers } from "./supplierApi";

const blankForm = {
  firstName: "",
  lastName: "",
  phoneNumber: "",
  email: "",
  password: "",
  brand: "",
  model: "",
  plateNumber: "",
  color: "",
  year: "",
};

function driverToForm(driver) {
  return {
    firstName: driver?.firstName || "",
    lastName: driver?.lastName || "",
    phoneNumber: driver?.phoneNumber || "",
    email: driver?.email || "",
    password: "",
    brand: driver?.car?.brand || "",
    model: driver?.car?.model || "",
    plateNumber: driver?.car?.plateNumber || "",
    color: driver?.car?.color || "",
    year: driver?.car?.year || "",
  };
}

export default function SupplierDriversPage() {
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blankForm);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setDrivers(await loadSupplierDrivers());
    } catch (e) {
      console.error("Supplier drivers load failed:", e);
      setError(e?.message || "Unable to load your drivers.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return drivers;
    return drivers.filter((d) => [
      d.firstName,
      d.lastName,
      d.email,
      d.phoneNumber,
      d.car?.brand,
      d.car?.model,
      d.car?.plateNumber,
      d.car?.color,
      d.car?.year,
    ].some((v) => String(v || "").toLowerCase().includes(q)));
  }, [drivers, search]);

  const openCreate = () => {
    setEditing(null);
    setForm(blankForm);
    setDialogOpen(true);
    setError("");
    setSuccess("");
  };

  const openEdit = (driver) => {
    setEditing(driver);
    setForm(driverToForm(driver));
    setDialogOpen(true);
    setError("");
    setSuccess("");
  };

  const updateField = (field) => (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const saveDriver = async () => {
    if (!form.firstName.trim() || !form.lastName.trim() || !form.email.trim() || !form.brand.trim() || !form.model.trim() || !form.plateNumber.trim()) {
      setError("Please complete the required driver and vehicle fields.");
      return;
    }
    if (!editing && form.password.length < 6) {
      setError("A password of at least 6 characters is required for a new driver.");
      return;
    }

    setSaving(true);
    setError("");
    try {
      const payload = {
        email: form.email.trim().toLowerCase(),
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        phoneNumber: form.phoneNumber.trim(),
        car: {
          brand: form.brand.trim(),
          model: form.model.trim(),
          plateNumber: form.plateNumber.trim(),
          color: form.color.trim(),
          year: form.year.trim(),
        },
      };

      if (editing) {
        await callDriverFunction("updateDriverWithAuth", { uid: editing.id, ...payload });
        setSuccess("Driver and vehicle details updated successfully.");
      } else {
        await callDriverFunction("createDriverWithAuth", { ...payload, password: form.password });
        setSuccess("Driver created successfully.");
      }

      setDialogOpen(false);
      await refresh();
    } catch (e) {
      setError(e?.message || "Unable to save driver.");
    } finally {
      setSaving(false);
    }
  };

  const deleteDriver = async () => {
    if (!deleteTarget) return;
    setSaving(true);
    setError("");
    try {
      await callDriverFunction("deleteDriverWithAuth", { uid: deleteTarget.id });
      setSuccess("Driver deleted successfully.");
      setDeleteTarget(null);
      await refresh();
    } catch (e) {
      setError(e?.message || "Unable to delete driver.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <Title title="My Drivers & Cars" />
      <PageHeader
        icon={<PeopleAltOutlinedIcon />}
        title="My Drivers & Cars"
        subtitle="Create and manage only the chauffeurs and vehicles registered under your supplier account."
        onRefresh={refresh}
        refreshing={loading}
        action={<Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>Add Driver</Button>}
      />

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess("")}>{success}</Alert>}

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={4}>
          <Paper elevation={0} sx={{ p: 2, borderRadius: 3 }}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Box sx={{ width: 44, height: 44, borderRadius: 2, display: "grid", placeItems: "center", bgcolor: "rgba(211,47,47,0.08)", color: "primary.main" }}>
                <PeopleAltOutlinedIcon />
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Registered drivers</Typography>
                <Typography variant="h5">{drivers.length}</Typography>
              </Box>
            </Stack>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Paper elevation={0} sx={{ p: 2, borderRadius: 3 }}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Box sx={{ width: 44, height: 44, borderRadius: 2, display: "grid", placeItems: "center", bgcolor: "rgba(211,47,47,0.08)", color: "secondary.main" }}>
                <DirectionsCarOutlinedIcon />
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Vehicles in your portal</Typography>
                <Typography variant="h5">{drivers.filter((d) => d.car?.plateNumber).length}</Typography>
              </Box>
            </Stack>
          </Paper>
        </Grid>
      </Grid>

      <Paper elevation={0} sx={{ p: { xs: 1.5, md: 2 }, borderRadius: 3 }}>
        <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={1} sx={{ mb: 1.5 }}>
          <TextField
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search driver, email, plate or vehicle"
            InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1, color: "text.secondary" }} /> }}
            sx={{ width: { xs: "100%", sm: 420 } }}
          />
          <Chip label="Supplier-owned records only" variant="outlined" color="primary" />
        </Stack>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}><CircularProgress /></Box>
        ) : filtered.length === 0 ? (
          <Box sx={{ py: 7, textAlign: "center" }}>
            <PeopleAltOutlinedIcon sx={{ fontSize: 46, color: "text.disabled" }} />
            <Typography sx={{ fontWeight: 800, mt: 1 }}>No drivers found</Typography>
            <Typography variant="body2" color="text.secondary">Add your first chauffeur to begin assigning rides.</Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small" sx={{ minWidth: 920 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Driver</TableCell>
                  <TableCell>Contact</TableCell>
                  <TableCell>Vehicle</TableCell>
                  <TableCell>Plate</TableCell>
                  <TableCell>Year / Color</TableCell>
                  <TableCell align="right">Manage</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.map((driver) => (
                  <TableRow key={driver.id} hover>
                    <TableCell>
                      <Typography sx={{ fontWeight: 800 }}>{driver.firstName} {driver.lastName}</Typography>
                      <Typography variant="caption" color="text.secondary">UID: {driver.id.slice(0, 8)}…</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontSize: 13 }}>{driver.email || "—"}</Typography>
                      <Typography variant="caption" color="text.secondary">{driver.phoneNumber || "—"}</Typography>
                    </TableCell>
                    <TableCell>{driver.car?.brand || "—"} {driver.car?.model || ""}</TableCell>
                    <TableCell><Chip size="small" label={driver.car?.plateNumber || "No plate"} variant="outlined" /></TableCell>
                    <TableCell>{driver.car?.year || "—"} · {driver.car?.color || "—"}</TableCell>
                    <TableCell align="right">
                      <Tooltip title="Edit driver and vehicle"><IconButton size="small" onClick={() => openEdit(driver)}><EditOutlinedIcon fontSize="small" /></IconButton></Tooltip>
                      <Tooltip title="Delete driver"><IconButton size="small" color="error" onClick={() => setDeleteTarget(driver)}><DeleteOutlineIcon fontSize="small" /></IconButton></Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <Dialog open={dialogOpen} onClose={() => !saving && setDialogOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>{editing ? "Edit Driver & Vehicle" : "Add Driver & Vehicle"}</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            This form is restricted to your own supplier account. Supplier ownership cannot be changed here.
          </Alert>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}><TextField fullWidth required label="First Name" value={form.firstName} onChange={updateField("firstName")} /></Grid>
            <Grid item xs={12} md={6}><TextField fullWidth required label="Last Name" value={form.lastName} onChange={updateField("lastName")} /></Grid>
            <Grid item xs={12} md={6}><TextField fullWidth required type="email" label="Driver Email" value={form.email} onChange={updateField("email")} /></Grid>
            <Grid item xs={12} md={6}><TextField fullWidth label="Phone Number" value={form.phoneNumber} onChange={updateField("phoneNumber")} /></Grid>
            {!editing && <Grid item xs={12}><TextField fullWidth required type="password" label="Initial Password" value={form.password} onChange={updateField("password")} helperText="Minimum 6 characters" /></Grid>}

            <Grid item xs={12}><Typography sx={{ fontWeight: 900, mt: 1 }}>Vehicle Details</Typography></Grid>
            <Grid item xs={12} md={6}><TextField fullWidth required label="Brand" value={form.brand} onChange={updateField("brand")} /></Grid>
            <Grid item xs={12} md={6}><TextField fullWidth required label="Model" value={form.model} onChange={updateField("model")} /></Grid>
            <Grid item xs={12} md={6}><TextField fullWidth required label="Plate Number" value={form.plateNumber} onChange={updateField("plateNumber")} /></Grid>
            <Grid item xs={12} md={3}><TextField fullWidth label="Year" value={form.year} onChange={updateField("year")} /></Grid>
            <Grid item xs={12} md={3}><TextField fullWidth label="Color" value={form.color} onChange={updateField("color")} /></Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={saveDriver} disabled={saving}>{saving ? "Saving..." : (editing ? "Save Changes" : "Create Driver")}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(deleteTarget)} onClose={() => !saving && setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Driver?</DialogTitle>
        <DialogContent>
          <Typography>
            Delete <strong>{deleteTarget?.firstName} {deleteTarget?.lastName}</strong> and remove the driver's login account? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={saving}>Cancel</Button>
          <Button variant="contained" color="error" onClick={deleteDriver} disabled={saving}>Delete Driver</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
